import { NavigationContainer, NavigationContainerRef } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState } from 'react-native';
import SCREENS from '~constants/screens';
import AppRoot from './AppRoot';
import AuthScreens from './AuthScreens';
import OnboardingScreens from './OnboardingScreens';
import { useGetUserAccount } from '~hooks/user/useGetUser';
import LoadingScreen from '~containers/Loading';
import { useGetBusinessAccount } from '~hooks/business/useGetBusinessAccount';
import { useMapsContext } from '~providers/maps/zustand';
import { ErrorBoundary } from '~components/ErrorBoundary';
import useGetCurrentPosition from '~components/LocationModal/hooks/useGetCurrentPosition';
import { useGetUserType } from '~hooks/event/useGetUserType';
import { useUserStore } from '~providers/userStore/zustand';
import VersionCheck from 'react-native-version-check';
import { Platform } from 'react-native';

import ChatProvider from '~providers/chats/context';
import axios from 'axios';
import UpdateAlert from '~components/UpdateAlert/UpdateAlert';
import { ThemedStatusBar } from '~components/ThemedStatusBar/ThemedStatusBar';
import { navigation, loading, auth as authLogger, timing, success } from '~Utils/debugLogger';
import { useOfflineUserData } from '~hooks/offline/useOfflineUserData';
import { logScreenView, setUserId, setUserProperties, initAnalytics, logEvent } from '~Utils/firebaseAnalytics';
import { ANALYTICS_EVENTS } from '~constants';

// 5 mins
const SECONDS_TO_REFETCH_LOCATION = 300;
export const navigationRef = React.createRef<NavigationContainerRef<any>>();
const Navigation = () => {
  console.log('🧭 [DEBUG] Navigation component started rendering - basic log');
  try {
    navigation('Navigation component started rendering');
  } catch (error) {
    console.error('❌ [DEBUG] Error with navigation logger:', error);
  }
  const navigationStartTime = Date.now();

  const { setCurrentPositionState, currentPositionState } = useMapsContext();
  const { getCurrentPosition, getLocationName } = useGetCurrentPosition();
  const Stack = createNativeStackNavigator();
  const user = useUserStore(state => state.user);
  const [isAuth, setIsAuth] = useState(!!user);
  // Use Zustand user for userId
  const userId = user?.uid || '';
  const [appState, setAppState] = useState(AppState.currentState);

  navigation('Starting API calls for user data');
  const { data: userType, isLoading: userTypeIsLoading } = useGetUserType(userId);
  const { data, isLoading: userAccountIsLoading } = useGetUserAccount(userId);
  const { data: businessData, isLoading: businessIsLoading } = useGetBusinessAccount(userId);

  // Get offline user data for fallback when offline
  const offlineUserData = useOfflineUserData();

  const [isNeedToUpdate, setIsNeedToUpdate] = useState(false); // Changed to false to prevent infinite loading
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [updateStoreUrl, setUpdateStoreUrl] = useState('');

  const { setUser, token } = useUserStore();

  const isBusiness = userType === 'business';

  useEffect(() => {
    const initialize = async () => {
      await initAnalytics();
      logEvent(ANALYTICS_EVENTS.APP_OPENED);
    };
    initialize();
  }, []);

  useEffect(() => {
    if (userId) {
      setUserId(userId);
      setUserProperties({
        user_type: userType || 'unknown',
        is_business: isBusiness.toString(),
      });
    } else {
      setUserId(null);
    }
  }, [userId, userType, isBusiness]);

  const showUpdateModal = useCallback((url: string) => {
    setUpdateStoreUrl(url);
    setShowUpdateAlert(true);
  }, []);

  const hideUpdateModal = useCallback(() => {
    setShowUpdateAlert(false);
    setUpdateStoreUrl('');
    setIsNeedToUpdate(false); // Reset the update flag so app can proceed
  }, []);

  const getIOSProvider = async (appStoreURL: string) => {
    try {
      const response = await axios.get(appStoreURL, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 16_0 like Mac OS X)',
        },
      });
      const data = response.data;
      return data?.resultCount > 0 ? data.results : null;
    } catch (error) {
      return null;
    }
  };

  const checkVersionAndAskUpdate = useCallback(async () => {
    navigation('Starting version check');
    const versionCheckStart = Date.now();

    // Skip version check on web - native module not available
    if (Platform.OS === 'web') {
      navigation('Skipping version check on web platform');
      return;
    }

    if (Platform.OS == 'android') {
      VersionCheck.needUpdate().then(async res => {
        timing('Android version check completed', versionCheckStart);
        navigation('Android version check result', res);

        setIsNeedToUpdate(res?.isNeeded);
        if (res?.isNeeded) {
          showUpdateModal(res.storeUrl);
        }
      });
    } else {
      const country = await VersionCheck.getCountry();
      const packageName = VersionCheck.getPackageName();
      const appStoreURL = `https://itunes.apple.com/${country}/lookup?bundleId=${packageName}`; // iTunes API URL of your package
      console.log(appStoreURL, 'appStoreURLappStoreURL');

      getIOSProvider(appStoreURL).then(res => {
        timing('iOS version check completed', versionCheckStart);
        navigation('iOS version check result', res[0]?.version);
        console.log(res[0].version, 'getIOSProvidergetIOSProvider');
        if (res[0]) {
          if (res[0].version) {
            VersionCheck.needUpdate({
              currentVersion: VersionCheck.getCurrentVersion(), // this returns the version setted on the last build
              latestVersion: res[0].version,
            }).then(resNeedUpdate => {
              navigation('Version update check result', resNeedUpdate);
              setIsNeedToUpdate(resNeedUpdate?.isNeeded);
              if (resNeedUpdate?.isNeeded) {
                showUpdateModal(res[0].trackViewUrl);
              }
            });
          }
        }
      });
    }
  }, [showUpdateModal]);

  useEffect(() => {
    checkVersionAndAskUpdate();
  }, [checkVersionAndAskUpdate]);

  useEffect(() => {
    if (data && !(data as unknown as { detail: string }).detail) {
      setUser(data);
    }
  }, [data, setUser]);

  // Handle AppState changes to prevent React Host context errors
  useEffect(() => {
    const handleAppStateChange = (nextAppState: string) => {
      if (appState.match(/inactive|background/) && nextAppState === 'active') {
        console.log('App has come to the foreground!');
        logEvent(ANALYTICS_EVENTS.APP_OPENED);
      } else if (nextAppState.match(/inactive|background/)) {
        console.log('App has gone to the background!');
      }
      setAppState(nextAppState as any);
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      subscription?.remove();
    };
  }, [appState]);

  useEffect(() => {
    if (isBusiness) {
      return;
    }

    let interval = setInterval(() => getCurrentPosition(true), SECONDS_TO_REFETCH_LOCATION * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [getCurrentPosition, isBusiness]);

  const fetchUserPosition = useCallback(async () => {
    if (!currentPositionState && data?.coords.lat && data?.coords.long) {
      if (data?.coords_real?.lat && data?.coords_real?.long) {
        const locationName = await getLocationName(data.coords_real.lat, data.coords_real.long);

        setCurrentPositionState({
          latitude: data.coords_real.lat,
          longitude: data.coords_real.long,
          address: locationName || '',
        });
      } else {
        const locationName = await getLocationName(data.coords.lat, data.coords.long);

        setCurrentPositionState({
          latitude: data.coords.lat,
          longitude: data.coords.long,
          address: locationName || '',
        });
      }

      getCurrentPosition(true, true);
    }
  }, [
    currentPositionState,
    data?.coords_real?.lat,
    data?.coords_real?.long,
    data?.coords?.lat,
    data?.coords?.long,
    getCurrentPosition,
    setCurrentPositionState,
    getLocationName,
  ]);

  const fetchBusinessPosition = useCallback(async () => {
    if (!currentPositionState && businessData?.coords.lat && businessData?.coords.long) {
      const locationName = await getLocationName(businessData.coords.lat, businessData.coords.long);

      setCurrentPositionState({
        latitude: businessData.coords.lat,
        longitude: businessData.coords.long,
        address: locationName || '',
      });
    }
  }, [
    businessData?.coords.lat,
    businessData?.coords.long,
    currentPositionState,
    getLocationName,
    setCurrentPositionState,
  ]);

  useEffect(() => {
    if (isBusiness) {
      fetchBusinessPosition();
    } else {
      fetchUserPosition();
    }
  }, [fetchUserPosition, fetchBusinessPosition, isBusiness]);

  const loadingIsActive = useMemo(() => {
    navigation('Checking loading states', {
      isAuth,
      userTypeIsLoading,
      userType,
      businessIsLoading,
      userAccountIsLoading,
      navigationTime: Date.now() - navigationStartTime,
    });

    if (!isAuth) {
      navigation('Not authenticated - no loading needed');
      return false;
    }

    if (userTypeIsLoading) {
      loading('User type is loading');
      return true;
    }

    if (userType === 'business' && businessIsLoading) {
      loading('Business data is loading');
      return true;
    } else if (userType === 'business' && !businessIsLoading) {
      success('Business data loaded');
      return false;
    }

    if (userType === 'personal' && userAccountIsLoading) {
      loading('Personal user data is loading');
      return true;
    } else if (userType === 'personal' && !userAccountIsLoading) {
      success('Personal user data loaded');
      return false;
    }

    success('All loading completed');
    return false;
  }, [isAuth, businessIsLoading, userAccountIsLoading, userType, userTypeIsLoading, navigationStartTime]);

  const screens = useMemo(() => {
    if (token) {
      return <Stack.Screen name={SCREENS.APP_ROOT} component={AppRoot} />;
    } else {
      return <Stack.Screen name={SCREENS.AUTH} component={AuthScreens} />;
    }
    // Use offline data as fallback when online data is not available
    const currentUserData = data || offlineUserData.userData;
    const currentBusinessData = businessData || offlineUserData.businessData;
    const currentUserType = userType || offlineUserData.userType;

    console.log('🔄 [DEBUG] Determining which screen to show:', {
      loadingIsActive,
      isNeedToUpdate,
    });

    if (loadingIsActive || isNeedToUpdate) {
      console.log('📱 [DEBUG] Showing LOADING screen');
      return <Stack.Screen name={SCREENS.LOADING} component={LoadingScreen} />;
    }
    if (!isAuth) {
      console.log('📱 [DEBUG] Showing AUTH screen');
      return <Stack.Screen name={SCREENS.AUTH} component={AuthScreens} />;
    }

    // Check registration status using current data (online or offline)
    const isRegistrationFinished =
      currentUserData?.is_registration_finished || currentBusinessData?.is_registration_finished;
    const hasOnboardingAnswers = currentUserData?.onboarding_answers && currentUserData.onboarding_answers.length > 0;

    if (!isRegistrationFinished) {
      console.log('📱 [DEBUG] Showing ONBOARDING screen (registration not finished)', {
        userRegistrationFinished: currentUserData?.is_registration_finished,
        businessRegistrationFinished: currentBusinessData?.is_registration_finished,
        usingOfflineData: !data && !!offlineUserData.userData,
      });
      return <Stack.Screen name={SCREENS.ONBOARDING} component={OnboardingScreens} />;
    }

    if (!hasOnboardingAnswers) {
      console.log('📱 [DEBUG] Showing ONBOARDING screen (no answers)', {
        onboardingAnswers: currentUserData?.onboarding_answers,
        answersLength: currentUserData?.onboarding_answers?.length || 0,
        usingOfflineData: !data && !!offlineUserData.userData,
      });
      return <Stack.Screen name={SCREENS.ONBOARDING} component={OnboardingScreens} />;
    }

    console.log('📱 [DEBUG] Showing APP_ROOT screen', {
      isRegistrationFinished,
      hasOnboardingAnswers,
      usingOfflineData: !data && !!offlineUserData.userData,
    });
    return <Stack.Screen name={SCREENS.APP_ROOT} component={AppRoot} />;
  }, [
    loadingIsActive,
    isAuth,
    data?.is_registration_finished,
    data?.onboarding_answers,
    businessData?.is_registration_finished,
    offlineUserData.userData?.is_registration_finished,
    offlineUserData.userData?.onboarding_answers,
    offlineUserData.businessData?.is_registration_finished,
    offlineUserData.hasOfflineData,
    offlineUserData.isOffline,
    userType,
    offlineUserData.userType,
    Stack,
    isNeedToUpdate,
    user,
  ]);

  const routeNameRef = React.useRef<string>();

  return (
    <ErrorBoundary>
      <NavigationContainer
        ref={navigationRef}
        onReady={() => {
          routeNameRef.current = navigationRef.current?.getCurrentRoute()?.name;
        }}
        onStateChange={async () => {
          const previousRouteName = routeNameRef.current;
          const currentRouteName = navigationRef.current?.getCurrentRoute()?.name;

          if (previousRouteName !== currentRouteName && currentRouteName) {
            await logScreenView(currentRouteName, currentRouteName);
          }
          routeNameRef.current = currentRouteName;
        }}
      >
        <ChatProvider>
          <ThemedStatusBar />
          <Stack.Navigator screenOptions={{ headerShown: false, gestureEnabled: false, animation: 'default' }}>
            {screens}
          </Stack.Navigator>
          <UpdateAlert visible={showUpdateAlert} onDismiss={hideUpdateModal} storeUrl={updateStoreUrl} />
        </ChatProvider>
      </NavigationContainer>
    </ErrorBoundary>
  );
};

export default Navigation;
